import React, { useState, useEffect, useCallback } from "react";
import "./App.css";
import Preloader from "./components/Preloader";
import Header from "./components/Header";
import Hero from "./components/Hero";
import FoodMenu from "./components/FoodMenu";
import FullMenu from "./components/FullMenu";
import AboutUs from "./components/AboutUs";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import WhatsAppFloat from "./components/WhatsAppFloat";
import NotFound from "./components/NotFound"; // ✅ Add this import

function App() {
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState("home");
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [chefSpecials, setChefSpecials] = useState([]);
  const [popularItems, setPopularItems] = useState([]);
  const [apiError, setApiError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const checkMobile = () => setIsMobile(window.innerWidth <= 768);

  const fetchAllData = useCallback(async () => {
    try {
      await Promise.all([
        fetchCategories(),
        fetchMenuItems(),
        fetchChefSpecials(),
        fetchPopularItems(),
      ]);
      setApiError(null);
    } catch (error) {
      console.error("Error fetching data:", error);
      setApiError("Failed to load menu data. Please try again later.");
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 3000);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    fetchAllData();

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", checkMobile);
    };
  }, [fetchAllData]);

  useEffect(() => {
    const titles = {
      home: "Home - Ali Darbar",
      about: "About Us - Ali Darbar",
      fullmenu: "Full Menu - Ali Darbar",
      404: "Page Not Found - Ali Darbar",
    };
    document.title = titles[currentView] || "Ali Darbar";
  }, [currentView]);

  const fetchCategories = async () => {
    const response = await fetch(
      "https://alidarbar.pythonanywhere.com/api/categories/"
    );
    if (!response.ok) throw new Error("Failed to fetch categories");
    const data = await response.json();
    setCategories(data);
  };

  const fetchMenuItems = async () => {
    const response = await fetch(
      "https://alidarbar.pythonanywhere.com/api/items/"
    );
    if (!response.ok) throw new Error("Failed to fetch menu items");
    const data = await response.json();
    setMenuItems(data);
  };

  const fetchChefSpecials = async () => {
    const response = await fetch(
      "https://alidarbar.pythonanywhere.com/api/items/chef-specials/"
    );
    if (!response.ok) throw new Error("Failed to fetch chef specials");
    const data = await response.json();
    setChefSpecials(data);
  };

  const fetchPopularItems = async () => {
    const response = await fetch(
      "https://alidarbar.pythonanywhere.com/api/items/popular/"
    );
    if (!response.ok) throw new Error("Failed to fetch popular items");
    const data = await response.json();
    setPopularItems(data);
  };

  const navigateTo = (view) => {
    setCurrentView(view);
    window.scrollTo(0, 0);
  };

  const scrollToSection = (sectionId) => {
    if (currentView === "home") {
      const element = document.getElementById(sectionId);
      if (element) element.scrollIntoView({ behavior: "smooth" });
    } else {
      setCurrentView("home");
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) element.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  };

  if (loading) return <Preloader />;

  // Render content based on currentView
  const renderView = () => {
    switch (currentView) {
      case "home":
        return (
          <>
            <Hero
              onViewFullMenu={() => navigateTo("fullmenu")}
              isMobile={isMobile}
            />
            <FoodMenu
              onViewFullMenu={() => navigateTo("fullmenu")}
              categories={categories}
              menuItems={menuItems}
              chefSpecials={chefSpecials}
              popularItems={popularItems}
              apiError={apiError}
              isMobile={isMobile}
            />
            <Contact isMobile={isMobile} />
          </>
        );
      case "fullmenu":
        return (
          <FullMenu
            onBackToHome={() => navigateTo("home")}
            categories={categories}
            menuItems={menuItems}
            apiError={apiError}
            isMobile={isMobile}
          />
        );
      case "about":
        return (
          <AboutUs
            onBackToHome={() => navigateTo("home")}
            isMobile={isMobile}
          />
        );
      default:
        return (
          <NotFound
            onBackToHome={() => navigateTo("home")}
            isMobile={isMobile}
          />
        );
    }
  };

  return (
    <div className="App">
      <Header
        currentView={currentView}
        onNavigate={navigateTo}
        onScrollToSection={scrollToSection}
        isMobile={isMobile}
      />

      {renderView()}

      <Footer isMobile={isMobile} />
      <WhatsAppFloat />
    </div>
  );
}

export default App;
